import { Injectable, signal } from "@angular/core";
import { CopyValueToClipboardService } from "src/app/services/copy-value-to-clipboard.service";
import { normalizeBranchInput } from "src/app/services/feature-env/feature-env-input";
import { readRecentFeatureEnvs, recentBranchesForRepo } from "src/app/services/feature-env/feature-env-recents";
import { KNOWN_REPOS } from "src/app/services/feature-env/feature-env.model";
import { FeatureEnvService } from "src/app/services/feature-env/feature-env.service";

export interface DevFeatureEnvMessage {
  readonly tone: "error";
  readonly text: string;
}

/**
 * State and handlers for the dev panel's feature environment rows.
 *
 * A separate injectable rather than a child component: DevSectionDirective
 * collects its rows with contentChildren, so rows rendered inside a child
 * component's own template would never be found and filtering would miss them.
 * The rows therefore stay in dev-panel-modal.component.html and only the logic
 * moves out.
 */
@Injectable({ providedIn: "root" })
export class DevFeatureEnvController {
  private readonly inputs = signal<Record<string, string>>({});
  private readonly messages = signal<Record<string, DevFeatureEnvMessage | undefined>>({});
  private readonly recents = signal(readRecentFeatureEnvs());

  readonly repos = KNOWN_REPOS;

  constructor(
    private readonly featureEnvService: FeatureEnvService,
    private readonly copyValueToClipboardService: CopyValueToClipboardService,
  ) {}

  activeBranch(repo: string): string | undefined {
    return this.featureEnvService.branchFor(repo);
  }

  hasActiveEnv(): boolean {
    return this.featureEnvService.active().length > 0;
  }

  input(repo: string): string {
    return this.inputs()[repo] ?? "";
  }

  message(repo: string): DevFeatureEnvMessage | undefined {
    return this.messages()[repo];
  }

  recentsFor(repo: string): string[] {
    return recentBranchesForRepo(this.recents(), repo);
  }

  setInput(repo: string, value: string): void {
    this.inputs.update((current) => ({ ...current, [repo]: value }));
    // Stale errors are worse than no errors — drop it the moment they retype.
    this.messages.update((current) => ({ ...current, [repo]: undefined }));
  }

  apply(repo: string): void {
    const result = normalizeBranchInput(this.input(repo));

    if (!result.branch) {
      if (result.error) {
        this.setMessage(repo, { tone: "error", text: result.error });
      }
      return;
    }

    this.featureEnvService.apply(repo, result.branch);
    this.featureEnvService.reload();
  }

  clear(repo: string): void {
    this.featureEnvService.clear(repo);
    this.featureEnvService.reload();
  }

  copyShareLink(): void {
    const link = this.featureEnvService.shareLink();
    if (!link) {
      return;
    }
    this.copyValueToClipboardService.copy(link, "Link copied");
  }

  private setMessage(repo: string, message: DevFeatureEnvMessage): void {
    this.messages.update((current) => ({ ...current, [repo]: message }));
  }
}
